/* The contribution/attribution layer (layout spec §12.2 backend ledger item 1 —
 * "the single highest-leverage unbuilt thing"). Built entirely at the DERIVE
 * layer, no backend change required: the run payload already carries the two
 * seeds the spec names —
 *   - IMPACT SERP results carry a per-result `ownership` tag (owned / resembler
 *     / not_owned) — state.serp / state.serps.
 *   - AIQ `citedDomains[].mappedAssetIds` links a cited domain back to the
 *     asset(s) that domain belongs to.
 * This module joins both onto `state.assets` by domain, so every property node
 * on the presence map, every reputation-surface card, and the drawer can show a
 * MEASURED contribution badge instead of an unmeasured blank.
 *
 * Pure + state-first (mirrors derive/search.ts, derive/ai.ts): every function
 * takes `state: AppPayloadState` as its first argument, no DOM/fetch/globals —
 * so it derives identically whichever payload is loaded into payloadStore,
 * whether that's a live `/clients/{slug}/overview`-driven run the service just
 * served, or an archived/dropped run JSON. Nothing here is fixture-specific.
 *
 * HONESTY: News is never a number. IMPACT News has no data plane wired into the
 * payload (docs/superpowers/specs/2026-07-16-client-hub-layout-design.md §8) —
 * `newsCount` is always `null`, never a fabricated 0, and callers must render
 * "not wired" rather than "N0".
 */
import { domainOf } from '../lib/format.ts';
import type { AppPayloadState } from './state-shape.ts';
import type { MatrixEntityRow } from './entity.ts';
import type { NormalizedAsset, SerpCapture, SerpResult } from '../types/contract.ts';

/** Integer percentage partition via largest remainder (FIX-audit F5). Given a
 * set of counts, returns whole-number percentages that sum EXACTLY to 100 when
 * the counts sum to > 0 (each independently `Math.round`ed can drift to 101/102
 * — e.g. [1,1,1,5]/8 → 13+13+13+63 = 102). Floors every share, then hands the
 * leftover points to the largest fractional remainders. All-zero → all-zero. */
export function pctPartition(counts: readonly number[]): number[] {
  const total = counts.reduce((s, n) => s + n, 0);
  if (total <= 0) return counts.map(() => 0);
  const exact = counts.map((n) => (n / total) * 100);
  const out = exact.map((x) => Math.floor(x));
  let rem = 100 - out.reduce((s, n) => s + n, 0); // whole points still to distribute
  const order = exact
    .map((x, i) => ({ i, frac: x - Math.floor(x) }))
    .sort((a, b) => b.frac - a.frac);
  for (let k = 0; k < order.length && rem > 0; k++, rem--) {
    const idx = order[k]!.i;
    out[idx] = (out[idx] ?? 0) + 1;
  }
  return out;
}

export interface AssetContribution {
  assetId: string;
  /** how many tracked SERP results (across all captures) resolved to this
   * asset's domain. */
  searchAppearances: number;
  /** best (lowest) rank observed, alongside the page it was seen on. */
  bestPosition: number | null;
  bestPage: number | null;
  /** the strongest ownership tag ever observed for this asset's domain
   * (owned > resembler > not_owned). */
  ownership: 'owned' | 'resembler' | 'not_owned' | null;
  /** AI citations attributed to this asset — an AIQ cited domain's count SPLIT
   * evenly across every asset it maps to (mappedAssetIds.length), so a domain
   * shared by many properties (e.g. 22 elal.com subpages) never re-shows its
   * full total on each one. An honest per-property estimate, not the domain
   * grand total. */
  aiCitations: number;
  /** which cited domains contributed to aiCitations, for tooltip/drawer detail. */
  aiDomains: string[];
  /** always null — IMPACT News carries no data plane; never a fabricated 0. */
  newsCount: null;
  /** true iff this property was measured surfacing anywhere (search or AI). */
  surfacing: boolean;
}

export type ContributionMap = Map<string, AssetContribution>;

function blank(id: string): AssetContribution {
  return {
    assetId: id, searchAppearances: 0, bestPosition: null, bestPage: null,
    ownership: null, aiCitations: 0, aiDomains: [], newsCount: null, surfacing: false,
  };
}

/** The distinct SERP captures the payload carries (FIX-audit F4). `serps[]` is
 * AUTHORITATIVE when populated — each entry is a distinct term×geo capture; the
 * single `serp` is a legacy/compat alias that, in some runs, DUPLICATES serps[0]
 * (the committed Ascensus fixture does exactly this: serp === serps[0], which
 * double-counted 17 page-1 slots and skewed ownership to 72/130 = 55% instead of
 * the true 68/120 = 57%). So prefer serps; fall back to [serp]. Then dedupe
 * defensively by query+location+capturedAt, so a capture repeated across the two
 * fields — or within serps — is only counted once. */
function serpCaptures(state: AppPayloadState): SerpCapture[] {
  const raw: (SerpCapture | null)[] = (state.serps && state.serps.length)
    ? state.serps
    : (state.serp ? [state.serp] : []);
  const seen = new Set<string>();
  const out: SerpCapture[] = [];
  for (const c of raw) {
    if (!c) continue;
    const key = `${c.query || ''}|${c.location || ''}|${c.capturedAt || ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(c);
  }
  return out;
}

/** Every SERP result across the distinct captures (deduped — see serpCaptures). */
function allSerpResults(state: AppPayloadState): SerpResult[] {
  const out: SerpResult[] = [];
  for (const c of serpCaptures(state)) out.push(...(c.results || []));
  return out;
}

const OWNERSHIP_RANK: Record<string, number> = { owned: 3, resembler: 2, not_owned: 1 };

/** Build the per-asset contribution map for the loaded payload. Idempotent and
 * cheap enough to call per-render (131 assets × ~17 SERP rows × 15 cited
 * domains — no memoization needed). */
export function computeContribution(state: AppPayloadState): ContributionMap {
  const map: ContributionMap = new Map();
  const byDomain = new Map<string, NormalizedAsset[]>();
  for (const a of state.assets) {
    const d = domainOf(a.url);
    if (!d) continue;
    const bucket = byDomain.get(d);
    if (bucket) bucket.push(a); else byDomain.set(d, [a]);
  }

  // ── search: join each tracked SERP result back to the asset(s) sharing its domain
  for (const r of allSerpResults(state)) {
    const d = domainOf(r.url);
    if (!d) continue;
    const matches = byDomain.get(d);
    if (!matches || !matches.length) continue;
    for (const a of matches) {
      const c = map.get(a.id) || blank(a.id);
      c.searchAppearances += 1;
      const page = r.page ?? 1;
      if (c.bestPage === null || page < c.bestPage || (page === c.bestPage && r.rank < (c.bestPosition ?? Infinity))) {
        c.bestPosition = r.rank; c.bestPage = page;
      }
      const curRank = c.ownership ? (OWNERSHIP_RANK[c.ownership] ?? 0) : 0;
      const nextRank = r.ownership ? (OWNERSHIP_RANK[r.ownership] ?? 0) : 0;
      if (nextRank > curRank) c.ownership = (r.ownership as AssetContribution['ownership']) ?? c.ownership;
      map.set(a.id, c);
    }
  }

  // ── AI: partition a cited domain's count across the properties it maps to.
  // BUG FIX (handoff P2): the branch's `Math.round(count / n)` rounded EACH
  // share up, so the per-asset shares SUMMED TO MORE than the domain's count —
  // a 15-citation domain split across 2 assets became 8+8=16, surfacing as the
  // "16 of 15 citations" rollup (numerator > denominator). It also meant a
  // domain mapped to nearly every asset stamped a near-full count on each,
  // producing the "AI36 on nearly every asset" inflation. Fixed with a
  // LARGEST-REMAINDER integer partition: floor share for all, then hand the
  // leftover `rem` citations to the first `rem` assets one at a time. The
  // shares now sum to EXACTLY d.count (disjoint slices), so no per-asset count
  // — nor any rollup of them — can ever exceed the domain total or the AIQ
  // grand total. Deterministic order (mappedAssetIds order) keeps it stable
  // across renders. dedupeIds guards a domain that lists the same asset twice
  // (would otherwise let one asset collect two slices).
  const doms = state.aiNarrative?.citedDomains || [];
  const assetIds = new Set(state.assets.map((a) => a.id));
  for (const d of doms) {
    const ids = [...new Set((d.mappedAssetIds || []).filter((id) => assetIds.has(id)))];
    if (!ids.length) continue;
    const base = Math.floor(d.count / ids.length);
    const rem = d.count - base * ids.length; // 0 .. ids.length-1, exact remainder
    ids.forEach((id, i) => {
      const share = base + (i < rem ? 1 : 0); // first `rem` assets get one extra
      const c = map.get(id) || blank(id);
      c.aiCitations += share;
      if (share > 0) c.aiDomains.push(d.domain); // provenance only for a real slice
      map.set(id, c);
    });
  }

  for (const c of map.values()) c.surfacing = c.searchAppearances > 0 || c.aiCitations > 0;
  return map;
}

export function contributionFor(map: ContributionMap, assetId: string): AssetContribution | null {
  return map.get(assetId) || null;
}

/** Compact node badge grammar (layout spec §5: "a compact ranking/contribution
 * chip per property (`P1·pos2`, `S4·AI12·N2`) or a red `not surfacing ⚠` for
 * dark assets"). News is deliberately OMITTED from the text (never a
 * fabricated N0) — the `dark` flag drives the caller's red/amber styling. */
export function contributionBadge(c: AssetContribution | null): { text: string; dark: boolean } {
  if (!c || !c.surfacing) return { text: 'not surfacing ⚠', dark: true };
  return { text: `S${c.searchAppearances}·AI${c.aiCitations}`, dark: false };
}

export function rankChip(c: AssetContribution | null): string | null {
  if (!c || c.bestPosition == null) return null;
  return `P${c.bestPage ?? 1}·pos${c.bestPosition}`;
}

/* ── entity-level rollups (presence-map entity header + the reputation cards'
   "by entity" disclosure) — sums the per-asset contribution over whichever
   assets a MatrixEntityRow's predicate matches. Entirely derived from the same
   measured map; no separate attribution model. ── */
export interface EntityContribution {
  key: string;
  name: string | null;
  assetCount: number;
  searchAppearances: number;
  aiCitations: number;
  surfacingCount: number;
}

export function entityContribution(state: AppPayloadState, map: ContributionMap, row: MatrixEntityRow): EntityContribution {
  let assetCount = 0, searchAppearances = 0, aiCitations = 0, surfacingCount = 0;
  for (const a of state.assets) {
    if (!row.match(a)) continue;
    assetCount++;
    const c = map.get(a.id);
    if (!c) continue;
    searchAppearances += c.searchAppearances;
    aiCitations += c.aiCitations;
    if (c.surfacing) surfacingCount++;
  }
  return { key: row.key, name: row.name, assetCount, searchAppearances, aiCitations, surfacingCount };
}

/* ── §7 "How the Reputation Surfaces" — Search card's measured attribution
   stack (owned / editable / media / adversarial). Every page-1 SERP result is
   joined back to the asset it resolves to (by domain); an unmatched result is
   `adversarial` only when it was itself negatively-rated or an impersonator
   ("resembler"), otherwise `media` (earned/third-party, not adversarial by
   default — never inflate the adversarial bucket from silence). Measured
   fact, not a model guess: every count traces to a real SERP row. ── */
export interface SearchAttribution {
  page1Owned: number;
  page1Slots: number;
  ownedPct: number;
  stackPct: { owned: number; editable: number; media: number; adversarial: number };
}

export function searchAttribution(state: AppPayloadState): SearchAttribution | null {
  const results = allSerpResults(state).filter((r) => (r.page ?? 1) === 1);
  if (!results.length) return null;
  const byDomain = new Map<string, NormalizedAsset>();
  for (const a of state.assets) {
    const d = domainOf(a.url);
    if (d && !byDomain.has(d)) byDomain.set(d, a);
  }
  let owned = 0, editable = 0, media = 0, adversarial = 0;
  for (const r of results) {
    const d = domainOf(r.url);
    const a = d ? byDomain.get(d) : undefined;
    if (a?.category === 'owned') owned++;
    else if (a?.category === 'editable') editable++;
    else if (r.rating === 'negative' || r.ownership === 'resembler') adversarial++;
    else media++;
  }
  const total = results.length;
  const page1Owned = owned + editable;
  // FIX-audit F5: largest-remainder the 4 stack buckets so they sum to EXACTLY
  // 100 (independent Math.round drifts to 101/102 and fabricates bar volume).
  // The headline "page-1 ownership" is the owned+editable share of that exact
  // partition, so the stat and the stack can never disagree.
  const [ownedP, editableP, mediaP, adversarialP] = pctPartition([owned, editable, media, adversarial]) as [number, number, number, number];
  return {
    page1Owned, page1Slots: total, ownedPct: ownedP + editableP,
    stackPct: { owned: ownedP, editable: editableP, media: mediaP, adversarial: adversarialP },
  };
}
